import {Table, Typography} from "antd";
import {CaruzEnrollment} from "../../models/CaruzEnrollment";
import {useEffect, useLayoutEffect, useState} from "react";
import './Participants.css';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {CHANGE_COMP} from "../../store";
import {FOUR_SPORT_LIVE_URL} from "../../constants";
import {DisplayState} from "../../reducers/displayReducer";
import {ColumnDefinitions, ColumnKey} from "../../constants/display-columns";
import {getCurrentTimeMills, MILLS_IN_MINUTE, timeToMills} from "../../utils";
import {alpha3ToAlpha2} from "../../utils/country-codes";

const {Text} = Typography

interface IEnrollmentsResponse {
    caruzEnrollments: CaruzEnrollment[]
}

interface IState {
    displayReducer: DisplayState
}

export function Participants() {
    const dispatch = useDispatch()
    const { comp: compQueryParam } = useParams() as { comp?: string };
    const comp = compQueryParam ? +compQueryParam : null;
    const [caruzEnrollments, setCaruzEnrollments] = useState<CaruzEnrollment[]>([])
    const selectedColumns = useSelector((state: IState) => state.displayReducer.SelectedColumns);
    const contentFontSize = useSelector((state: IState) => state.displayReducer.ContentFontSize);
    const headerFontSize = useSelector((state: IState) => state.displayReducer.HeaderFontSize);
    const selectedLocations = useSelector((state: IState) => state.displayReducer.SelectedLocations);
    const [render, setRender] = useState(1)
    const navigate = useNavigate()
    const includeUnrecognizedChips = useSelector((state: IState) => state.displayReducer.IncludeUnrecognizedChips);

    useEffect(()=> {
        if(!comp) {
            navigate('/events')
            return
        }
        let timer: NodeJS.Timeout
        fetch(`${FOUR_SPORT_LIVE_URL}/caruz/getCaruzEnrollment/`, {
            method: 'post',
            headers: {
                'Accept': 'application/json',
                'Authorization': 'Token ' + localStorage.getItem('caruzToken')
            },
            body: JSON.stringify({
                locations: selectedLocations,
                includeUnrecognizedChips,
                comp
            })
        }).then(response => response.json()).then((response: IEnrollmentsResponse) => {
            setCaruzEnrollments(CaruzEnrollment.instantiateFromArr(response.caruzEnrollments))
            timer = setTimeout(() => setRender((render) => render + 1) , 500)
        }).catch((error: any) => {
            console.log(error)
            timer = setTimeout(() => setRender((render) => render + 1) , 500)
        })
        return () => clearTimeout(timer);
    }, [render, navigate, selectedLocations, comp, includeUnrecognizedChips])

    useEffect(() => {
        dispatch({
            type: CHANGE_COMP,
            payload: { comp }
        });
    }, [comp, dispatch]);


    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.set("contentFontSize", contentFontSize.toString());
        params.set("headerFontSize", headerFontSize.toString());

        params.delete("selectedColumns");
        selectedColumns.forEach(column => params.append("selectedColumns", column));

        params.delete("selectedLocations");
        selectedLocations.forEach(location => params.append("selectedLocations", location));

        const queryParams = params.toString();
        const newUrl = queryParams ? `${window.location.pathname}?${queryParams}` : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
    }, [selectedLocations, headerFontSize, contentFontSize, selectedColumns]);

    useEffect(() => {
        localStorage.setItem("caruz_contentFontSize", contentFontSize.toString());
        localStorage.setItem("caruz_headerFontSize", headerFontSize.toString());
        localStorage.setItem("caruz_selectedColumns", JSON.stringify(selectedColumns));
    }, [contentFontSize, headerFontSize, selectedColumns]);

    const getColumnsForUi = () => {
        const selectedColumnsSet = new Set(selectedColumns)
        return ColumnDefinitions
            .filter(column => selectedColumnsSet.has(column.key))
            .map(columnDefinition => {
                return {
                    title: <Text style={{fontSize: `${headerFontSize}px`, color: 'white'}}>{columnDefinition.description}</Text>,
                    text: columnDefinition.description,
                    dataIndex: columnDefinition.key,
                    key: columnDefinition.key,
                    width: columnDefinition.width,
                    align: columnDefinition.textAlign,
                    visible: true,
                    render: (_: string, caruzEnrollment: CaruzEnrollment) => {
                        const value = caruzEnrollment.getValueForColumnKey(columnDefinition.key);
                        if (columnDefinition.key === ColumnKey.Country) {

                            const iso2 = alpha3ToAlpha2(caruzEnrollment.country) ?? 'il';
                            const flagH = Math.max(12, Math.round(contentFontSize));
                            const flagW = Math.round((flagH * 4) / 3);
                            const radius = Math.max(2, Math.round(flagH * 0.12));

                            console.log(iso2.toLowerCase())

                            return (
                                <span
                                    className={`fi fi-${iso2.toLowerCase()}`}
                                    style={{ display: "inline-block", width: flagW, height: flagH, borderRadius: radius }}
                                />
                            );
                        }

                        if (columnDefinition.key === ColumnKey.SectionName) {
                            return <Text style={{fontSize: `${contentFontSize}px`, color: 'white'}}>{value}</Text>
                        }

                        return <Text style={{fontSize: `${contentFontSize}px`, color: 'white'}}>{value}</Text>
                    }
                }
            })
    }

    // Fallback estimate (in case we can't measure)
    const estimatedRowH = Math.round(contentFontSize + 24); // 12px top + 12px bottom padding from Participants.css
    const [measuredRowH, setMeasuredRowH] = useState<number | null>(null);

    // Measure the actual rendered first row height so the logo is exactly 3 rows tall
    useLayoutEffect(() => {
        const rowEl = document.querySelector('.participants-table .ant-table-tbody tr') as HTMLElement | null;
        if (!rowEl) return;
        const h = rowEl.getBoundingClientRect().height;
        if (!h || Number.isNaN(h)) return;
        if (measuredRowH === null || Math.abs(measuredRowH - h) > 0.5) {
            setMeasuredRowH(h);
        }
    }, [contentFontSize, headerFontSize, selectedColumns, caruzEnrollments.length, measuredRowH]);

    const rowHeight = Math.round((measuredRowH ?? estimatedRowH));
    const logoSrc = `${process.env.PUBLIC_URL}/israman-logo.svg`;

    return (
        <div
            className="participants-layout"
            style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'flex-start',
                gap: 28,
                backgroundColor: '#0D0088',
                minHeight: '100vh',
                width: '100%',
            }}
        >
            <div
                className="participants-table"
                style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                }}
            >
                <Table
                    showHeader={false}
                    dataSource={caruzEnrollments}
                    columns={getColumnsForUi()}
                    pagination={false}

                    rowClassName={(record, index) => {
                        const oddEvenClass = index % 2 === 0 ? 'odd-row' : '';

                        // not the latest record
                        if(index > 0) {
                            return oddEvenClass
                        }

                        // unrecognized record
                        if(!record.number) {
                            return oddEvenClass
                        }

                        try {
                            const millsSinceRecordTime = getCurrentTimeMills() - timeToMills(record.time)
                            // not from the last minute
                            if(millsSinceRecordTime > MILLS_IN_MINUTE) {
                                return oddEvenClass
                            }

                            return `latest-row ${oddEvenClass}`;
                        }
                        catch (e) {
                            return oddEvenClass
                        }
                    }}
                />
            </div>
        </div>
    )
}